/**
 * Firebase configuration and initialization
 * Handles Firebase app setup and exports services
 */

import { initializeApp } from "firebase/app";
import { getFirestore, connectFirestoreEmulator } from "firebase/firestore";
import { getAuth, connectAuthEmulator } from "firebase/auth";
import { getStorage, connectStorageEmulator } from "firebase/storage";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from "firebase/app-check";

// Firebase configuration
// Replace with your actual Firebase config
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain:
    import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket:
    import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId:
    import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId:
    import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize App Check (only in browser environment)
if (typeof window !== "undefined") {
  const recaptchaSiteKey =
    import.meta.env.VITE_RECAPTCHA_SITE_KEY || "YOUR_RECAPTCHA_SITE_KEY";
  const enableAppCheck = import.meta.env.VITE_ENABLE_APP_CHECK !== "false";

  // Only initialize if site key is provided and App Check is enabled
  if (
    enableAppCheck &&
    recaptchaSiteKey &&
    recaptchaSiteKey !== "YOUR_RECAPTCHA_SITE_KEY"
  ) {
    try {
      // --- ส่วนที่เพิ่ม: เปิดใช้ Debug Token เฉพาะตอน Dev ---
      if (import.meta.env.DEV) {
        self.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
      }
      // ------------------------------------------------
      initializeAppCheck(app, {
        provider: new ReCaptchaEnterpriseProvider(recaptchaSiteKey),
        isTokenAutoRefreshEnabled: true,
      });

      if (import.meta.env.DEV) {
        console.log("[App Check] Initialized successfully");
      }
    } catch (error) {
      console.error("[App Check] Initialization error:", error);
    }
  } else if (import.meta.env.DEV) {
    if (!enableAppCheck) {
      console.warn(
        "[App Check] Disabled via VITE_ENABLE_APP_CHECK=false"
      );
    } else if (!recaptchaSiteKey || recaptchaSiteKey === "YOUR_RECAPTCHA_SITE_KEY") {
      console.warn(
        "[App Check] Not initialized - VITE_RECAPTCHA_SITE_KEY not set or invalid"
      );
    }
  }
}

// Initialize Firebase services
export const db = getFirestore(app);
export const auth = getAuth(app);
export const storage = getStorage(app);
export const functions = getFunctions(app, "asia-southeast1");

// Configure auth settings to avoid CORS issues
auth.settings.appVerificationDisabledForTesting = false;



// Export the app instance
export default app;

// Firebase collections constants
export const COLLECTIONS = {
  // DEPRECATED - DO NOT USE
  // CHATS: "chats",
  // MESSAGES: "messages", 
  // CHANNELS: "channels",
  
  USERS: "users",
  ANALYTICS: "analytics",
  ORGS: "orgs",
  TEAMS: "teams",
  BILLING: "billing",
  INTEGRATIONS: "integrations",
  AI_SETTINGS: "ai_settings",
  INVITES: "invites",
  // Multi-tenant collections (accessed via orgs/{orgId}/)
  INBOX: "inbox",
  CONVERSATIONS: "conversations",
};

// Policy version constant
export const CURRENT_POLICY_VERSION = 'v1';

// Firebase document field constants
export const FIELDS = {
  // Chat fields
  CUSTOMER_NAME: "customerName",
  CUSTOMER_EMAIL: "customerEmail",
  CUSTOMER_ID: "customerId",
  STATUS: "status",
  LAST_MESSAGE: "lastMessage",
  LAST_MESSAGE_TIME: "lastMessageTime",
  LAST_MESSAGE_SENDER: "lastMessageSender",
  UNREAD_COUNT: "unreadCount",
  CREATED_AT: "createdAt",
  UPDATED_AT: "updatedAt",
  HANDOFF_TIME: "handoffTime",

  // Message fields
  CONTENT: "content",
  TYPE: "type",
  SENDER: "sender",
  TIMESTAMP: "timestamp",
  IS_READ: "isRead",
  METADATA: "metadata",
};

// Chat status constants
export const CHAT_STATUS = {
  ACTIVE: "active",
  PENDING: "pending",
  CLOSED: "closed",
  HUMAN_HANDOFF: "human_handoff",
};

// Message type constants
export const MESSAGE_TYPE = {
  TEXT: "text",
  IMAGE: "image",
  FILE: "file",
  SYSTEM: "system",
};

// Sender constants
export const SENDER = {
  CUSTOMER: "customer",
  ADMIN: "admin",
  SYSTEM: "system",
};
